import { useEffect, useRef, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { format, parseISO } from "date-fns";

import {
  Box,
  Button,
  Checkbox,
  Chip,
  Link,
} from "@mui/material";
import { useSnackbar } from "notistack";

import Table from "../../../../components/Table";
import ProviderBadge from "../../../../components/ProviderBadge";
import InvoicesDetailsDrawer from "./InvoiceDetailsDrawer";
import InvoicesTableControls from "./TableControls";
import {
  selectInvoiceById,
  fetchInvoices,
  fetchInvoiceDetail,
  fetchInvoiceAttachments,
  setInvoiceVisibility,
  clearMessages,
  exportPaymentLinks,
} from "../../../../redux/slices/invoicesSlice";
import { selectCustomer } from "../../../../redux/slices/customersSlice";
import { canSwitchSource } from "../../../../redux/slices/sourceSlice";
import SendPaymentLinkDialog from "./SendPaymentLinkDialog";
import CustomersDrawer from "../../customers/components/CustomersDrawer";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";

const Invoices = () => {
  const dispatch = useAppDispatch();
  const {
    invoices,
    invoicesTotal,
    filters,
    fetchInvoicesStatus,
    updateInvoiceStatus,
  } = useAppSelector((state) => state.invoices) as any;
  const { enqueueSnackbar } = useSnackbar();

  // Pagination + sorting state (0-based page, matches the table)
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  // Which accounting system to list: the switch in the navbar decides it for
  // the whole portal, so the page only reads it.
  const { source } = useAppSelector((state) => state.source) as any;
  const user = useAppSelector((state) => state.login.user) as any;
  // The API route is admin-only (super-admin or admin), so the column follows.
  const canSendLinks = user?.role === "super-admin" || user?.role === "admin";
  // The row whose link is being sent, so the dialog can name its invoice.
  const [linkRow, setLinkRow] = useState<any>(null);
  // Which link export is running, if any — the button shows the spinner.
  const [exporting, setExporting] = useState<"main" | "bill" | null>(null);

  // Bumped after mutations (visibility toggle) so the current page refetches.
  const [refreshNonce, setRefreshNonce] = useState<number>(0);

  // Column search (debounced) — sent to the server as per-column filters.
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const debouncedColumnSearch = useDebouncedValue(columnSearch, 400);

  // Visibility toggles: show a snackbar and refetch the current page once per
  // toggle. Deliberately does NOT depend on fetchInvoicesStatus — coupling the
  // two caused an endless refetch loop (toggle -> refreshNonce -> refetch ->
  // fetchInvoicesStatus changes -> handler re-ran -> nonce bumped again).
  useEffect(() => {
    if (updateInvoiceStatus === "succeeded") {
      enqueueSnackbar("Invoice visibility updated successfully", {
        variant: "success",
      });
      // The visibility change invalidated the server report — refetch so the
      // row reflects the new state.
      setRefreshNonce((n) => n + 1);
    }
    if (updateInvoiceStatus === "failed") {
      enqueueSnackbar("Failed to update invoice visibility", {
        variant: "error",
      });
    }
  }, [enqueueSnackbar, updateInvoiceStatus]);

  // Fetch failures: surface a snackbar when the invoice list request fails, and
  // let the same request be sent again — a failed request is the one case where
  // asking for exactly what was just asked for is the point.
  useEffect(() => {
    if (fetchInvoicesStatus === "failed") {
      lastRequest.current = "";
      enqueueSnackbar("Failed to fetch invoices", {
        variant: "error",
      });
    }
  }, [enqueueSnackbar, fetchInvoicesStatus]);

  useEffect(() => {
    return () => {
      // Clear messages when component unmounts
      dispatch(clearMessages());
    };
  }, [dispatch]);

  // Everything that changes which invoices come back, as strings, so the fetch
  // below can compare one set of inputs with the last instead of one render
  // with the last.
  const paymentStatusKey = (filters?.invoices?.paymentStatus || []).join(",");
  const invoiceNumberKey = debouncedColumnSearch.invoiceNumber || "";
  const customerNameKey = debouncedColumnSearch.customerName || "";
  const filterKey = [paymentStatusKey, source, invoiceNumberKey, customerNameKey].join("|");

  // The filters the list on screen was fetched with. A change to them means the
  // list starts again at its first page, rather than asking for a page of a
  // list that no longer exists.
  const [fetchedFilterKey, setFetchedFilterKey] = useState(filterKey);
  // The last request sent, so the effect below cannot send the same one twice.
  // Reloading this page used to ask three times: once before the source switch
  // had answered, and then twice for one answer, because the filter key was
  // held in state and updated by an effect — the fetch ran once with the old
  // key and again with the new one.
  const lastRequest = useRef<string>("");

  // A user who cannot switch the source is never sent one, so their list must
  // not wait for it.
  const { settled: sourceSettled } = useAppSelector((state) => state.source) as any;
  const sourcePending = canSwitchSource(user) && !sourceSettled;

  useEffect(() => {
    if (sourcePending) return;

    const filtersChanged = fetchedFilterKey !== filterKey;
    const request = {
      page: filtersChanged ? 0 : page,
      pageSize: rowsPerPage,
      paymentStatus: paymentStatusKey ? paymentStatusKey.split(",") : undefined,
      invoiceNumber: invoiceNumberKey || undefined,
      customerName: customerNameKey || undefined,
      source: source || undefined,
      sortBy: sortBy || undefined,
      sortOrder,
    };
    // refreshNonce is not a parameter — it marks a mutation whose result has to
    // be read back — but it belongs in the comparison, or a visibility toggle
    // would be skipped as a repeat.
    const key = JSON.stringify({ ...request, refreshNonce });
    if (key === lastRequest.current) return;
    lastRequest.current = key;

    if (filtersChanged) {
      setFetchedFilterKey(filterKey);
      if (page !== 0) setPage(0);
    }
    dispatch(fetchInvoices(request));
  }, [
    dispatch,
    filterKey,
    fetchedFilterKey,
    page,
    rowsPerPage,
    paymentStatusKey,
    invoiceNumberKey,
    customerNameKey,
    sortBy,
    sortOrder,
    refreshNonce,
    source,
    sourcePending,
  ]);

  const handleVisibilityChange = (id: string | number) => (event: any) => {
    const isVisible = event.target.checked;
    dispatch(
      setInvoiceVisibility({
        providerId: id,
        isVisible,
      }),
    );
  };

  // The links for what the filters are showing, as one file — the office puts
  // them in the mail it sends itself rather than sending from the portal. The
  // server applies the same filters, so the file matches the list below it.
  //
  // The book is always the one on screen, so the rows in the file are the rows
  // in the table. The Bill.com button changes only which link the column
  // carries: the link that pays each of these invoices through Bill.com.
  const handleExportLinks = async (book?: string) => {
    setExporting(book === "bill" ? "bill" : "main");
    try {
      const result: any = await dispatch(
        exportPaymentLinks({
          paymentStatus: filters?.invoices?.paymentStatus,
          invoiceNumber: debouncedColumnSearch.invoiceNumber,
          customerName: debouncedColumnSearch.customerName,
          source,
          link: book === "bill" ? "bill" : undefined,
          sortBy: sortBy || undefined,
          sortOrder,
        }),
      );
      enqueueSnackbar(
        result?.error
          ? result.payload || "Could not export the links"
          : "Payment links exported",
        { variant: result?.error ? "error" : "success" },
      );
    } finally {
      setExporting(null);
    }
  };

  // Copying puts the link in the office's own mail, so it says which book the
  // link pays through rather than just "copied".
  const copyPaymentLink = async (link: string | undefined, book: string) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      enqueueSnackbar(`${book} link copied`, { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy the link", { variant: "error" });
    }
  };

  // The row carries one link: the one of the book being shown. The Bill.com
  // link is not offered per row for now — its file is in the controls above.
  const rowIsBill = source === "bill";
  const rowLink = (row: any) => (rowIsBill ? row.billPaymentLink : row.iesPaymentLink);

  return (
    <Box>
        <InvoicesTableControls onExport={handleExportLinks} exporting={exporting} />

        <Table
          enableColumnSearch
          serverPagination
          totalCount={invoicesTotal}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(key: string, order: "asc" | "desc") => {
            setSortBy(key);
            setSortOrder(order);
          }}
          onColumnSearchChange={setColumnSearch}
          maxHeight="calc(100vh - 220px)"
          headers={[
            { key: "invoiceNumber", displayName: "Number" },
            {
              key: "invoiceDate",
              displayName: "Invoice Date",
              disableColumnSearch: true,
              format: ({ value }) => format(parseISO(value), "MM/dd/yyyy"),
            },
            {
              key: "dueDate",
              displayName: "Due Date",
              disableColumnSearch: true,
              format: ({ value }) => format(parseISO(value), "MM/dd/yyyy"),
            },
            {
              key: "paymentStatus",
              displayName: "Status",
              disableColumnSearch: true,
              format: ({ value }) => {
                const label = {
                  0: "Paid in full",
                  1: "Open",
                  2: "Partial payment",
                  4: "Scheduled",
                };
                const color = {
                  0: "success",
                  1: "warning",
                  2: "info",
                  4: "secondary",
                };

                return (
                  <Chip
                    label={(label as any)[value] || "Unknown"}
                    variant="outlined"
                    color={(color as any)[value] || "default"}
                    size="small"
                  />
                );
              },
            },
            {
              key: "isVisible",
              displayName: "Visibility",
              disableColumnSearch: true,
              format: ({ value, row }) => {
                return (
                  <Checkbox
                    checked={!!value}
                    disabled={row.paymentStatus !== "1"}
                    size="small"
                    color="primary"
                    onChange={handleVisibilityChange(row.id)}
                  />
                );
              },
            },
            {
              key: "providerId",
              displayName: "Source",
              disableColumnSearch: true,
              format: ({ row }) => <ProviderBadge source={row?.source} providerId={row?.providerId ?? row?.id} />,
            },
            {
              key: "paymentLinkSentAt",
              displayName: "Link sent",
              disableColumnSearch: true,
              format: ({ value }) => {
                if (!value) return "—";
                try {
                  return format(parseISO(String(value)), "MM/dd/yyyy");
                } catch {
                  return "—";
                }
              },
            },
            ...(canSendLinks
              ? [
                  {
                    key: "sendPaymentLink",
                    displayName: "Send link",
                    disableColumnSearch: true,
                    format: ({ row }: any) => (
                      // A Box, not a Stack: the dashboard's layout paints the hero
                      // background on any Stack nested inside another, and a Stack
                      // here sits inside the layout's — see the & .MuiStack-root
                      // rule in routes/dashboard/index.tsx.
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button size="small" variant="outlined" onClick={() => setLinkRow(row)}>
                          Send
                        </Button>
                        {/* The link of the book being shown, so the button is
                            there wherever the office is working. The Bill.com
                            links are a file in the controls above, not a button
                            per row — for now. */}
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!rowLink(row)}
                          onClick={() =>
                            copyPaymentLink(rowLink(row), rowIsBill ? "Bill.com" : "IES")
                          }
                        >
                          {rowIsBill ? "Bill link" : "IES link"}
                        </Button>
                      </Box>
                    ),
                  },
                ]
              : []),
            {
              key: "customerName",
              displayName: "Customer Name",
              format: ({ value, row }) => {
                return (
                  <Link
                    href="#"
                    underline="hover"
                    color="inherit"
                    onClick={() => {
                      dispatch(selectCustomer(row.databaseId));
                    }}
                  >
                    {value}
                  </Link>
                );
              },
            },

            {
              key: "amount",
              displayName: "Amount",
              disableColumnSearch: true,
              format: ({ value }) =>
                value.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                }),
            },
            {
              key: "amountDue",
              displayName: "Balance Due",
              disableColumnSearch: true,
              format: ({ value }) =>
                (value ?? 0).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                }),
            },
          ]}
          rows={invoices}
          actions={{
            view: (id: string | number) => {
              dispatch(selectInvoiceById(id));
              dispatch(fetchInvoiceDetail(String(id)));
              dispatch(fetchInvoiceAttachments(String(id)));
            },
          }}
          loading={fetchInvoicesStatus === "loading"}
        />
        <InvoicesDetailsDrawer />
        <CustomersDrawer />
        {/* Who the link goes to is chosen here rather than assumed from the
            customer record: an account can have several people on it. */}
        <SendPaymentLinkDialog
          open={!!linkRow}
          onClose={() => setLinkRow(null)}
          invoiceId={linkRow?.id || ""}
          invoiceNumber={linkRow?.invoiceNumber}
          onSent={(result: any) => {
            const sentTo = Array.isArray(result?.to) ? result.to.join(", ") : result?.to;
            enqueueSnackbar(
              result?.failed?.length
                ? `Payment link sent to ${sentTo}. Could not reach ${result.failed.join(", ")}.`
                : `Payment link sent to ${sentTo ?? "the client"}`,
              { variant: result?.failed?.length ? "warning" : "success" },
            );
          }}
        />
    </Box>
  );
};

export default Invoices;
